import { CaptureControls } from './Controls';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { UserRoles } from '@bandido/shared/types';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { Link } from '@frontend/features/common/components/Link';
import { Table } from '@frontend/features/common/components/Table/Table';
import { routes } from '@frontend/router/routes';
import { filterSample, groupSample, localDateTime, reportRange, SampleFilters } from './model';
import { performanceError, usePerformanceConfiguration, usePerformanceReport } from './queries';
import { PerformanceReport } from './types';
import { PerformanceSettingsForm } from './SettingsForm';

function SampleReport({ report }: { report: PerformanceReport }) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<SampleFilters>({ advisor: '', team: '', origin: '' });
  const [view, setView] = useState<'cases' | 'advisor' | 'team'>('cases');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const visible = useMemo(() => filterSample(report.cases, filters), [report.cases, filters]);
  const none = t('workflowPerformance.unassigned');
  const options = (field: keyof SampleFilters) => [
    ...new Map(
      report.cases.map((row) => {
        const value = field === 'advisor' ? row.agentUserId : field === 'team' ? row.teamId : row.origin;
        const label =
          field === 'advisor'
            ? row.agentName
            : field === 'team'
              ? row.teamName
              : row.origin
                ? t(`cases.originatedByChannel.${row.origin}`, { defaultValue: row.origin })
                : null;
        return [value === null ? 'unassigned' : String(value), label ?? none];
      }),
    ).entries(),
  ];
  const columns =
    view === 'cases'
      ? [
        { id: 'case', label: t('workflowPerformance.case') },
        { id: 'agent', label: t('workflowPerformance.advisor') },
        { id: 'team', label: t('workflowPerformance.team') },
        { id: 'origin', label: t('workflowPerformance.origin') },
        { id: 'status', label: t('workflowPerformance.status') },
        { id: 'lastObserved', label: t('workflowPerformance.lastObserved') },
        { id: 'stateObservations', label: t('workflowPerformance.stateObservations') },
        { id: 'messageLinks', label: t('workflowPerformance.messageLinks') },
        { id: 'failedLinks', label: t('workflowPerformance.failedLinks') },
      ]
      : [
        { id: 'name', label: t(`workflowPerformance.${view}`) },
        { id: 'cases', label: t('workflowPerformance.sampleCases') },
        { id: 'needsAgent', label: t('workflowPerformance.needsAgent') },
        { id: 'stateObservations', label: t('workflowPerformance.stateObservations') },
        { id: 'messageLinks', label: t('workflowPerformance.messageLinks') },
      ];
  const rows =
    view === 'cases'
      ? visible.map((row) => ({
        ...row,
        id: row.caseId,
        case: <Link to={routes.dashboard.caseView.replace(':id', String(row.caseId))}>#{row.caseId}</Link>,
        agent: row.agentName ?? row.agentUserId ?? none,
        team: row.teamName ?? none,
        origin: row.origin ? t(`cases.originatedByChannel.${row.origin}`, { defaultValue: row.origin }) : none,
        status: (
          <Chip
            size="small"
            color={row.status === 'needsAgentAction' ? 'warning' : 'default'}
            label={t(`cases.status.${row.status}`, { defaultValue: row.status })}
          />
        ),
        lastObserved: row.lastObservedAt
          ? new Date(row.lastObservedAt).toLocaleString()
          : t('workflowPerformance.unavailableValue'),
      }))
      : groupSample(visible, view).map((row) => ({
        ...row,
        name: row.name ?? (row.id === 'unassigned' ? none : row.id),
      }));

  return (
    <Stack spacing={2}>
      <Typography variant="h5">{report.workflowName}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('workflowPerformance.generated', { at: new Date(report.generatedAt).toLocaleString() })}
      </Typography>
      <Typography variant="body2">
        {new Date(report.range.from).toLocaleString()} — {new Date(report.range.to).toLocaleString()}
      </Typography>
      <Alert severity="warning">{t('workflowPerformance.captureUnverified')}</Alert>
      <Alert severity="warning">{t('workflowPerformance.partial')}</Alert>
      {(report.truncated || report.runsTruncated) && (
        <Alert severity="warning">{t('workflowPerformance.truncated', { limit: report.sampleLimit })}</Alert>
      )}
      {(!report.runs.length || report.runs.some((run) => run.incomplete)) && (
        <Alert severity="warning">{t('workflowPerformance.captureGap')}</Alert>
      )}
      {!report.cases.length && <Alert severity="info">{t('workflowPerformance.empty')}</Alert>}
      <Alert severity="info">{t('workflowPerformance.unavailableMetrics')}</Alert>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {(['advisor', 'team', 'origin'] as const).map((field) => (
          <TextField
            select
            key={field}
            value={filters[field]}
            label={t(`workflowPerformance.${field}`)}
            sx={{ minWidth: 170 }}
            onChange={(event) => {
              setFilters({ ...filters, [field]: event.target.value });
              setPage(1);
            }}
          >
            <MenuItem value="">{t('workflowPerformance.all')}</MenuItem>
            {options(field).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        ))}
      </Stack>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        <Chip label={t('workflowPerformance.sampleCount', { count: visible.length })} />
        <Chip
          color="warning"
          label={t('workflowPerformance.pendingCount', {
            count: visible.filter((row) => row.status === 'needsAgentAction').length,
          })}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {t('workflowPerformance.groupingHint')}
      </Typography>
      <Tabs
        value={view}
        onChange={(_event, value) => {
          setView(value);
          setPage(1);
        }}
      >
        <Tab value="cases" label={t('workflowPerformance.cases')} />
        <Tab value="advisor" label={t('workflowPerformance.byAdvisor')} />
        <Tab value="team" label={t('workflowPerformance.byTeam')} />
      </Tabs>
      <Table
        height={420}
        columns={columns}
        data={{ key: 'id', items: rows.slice((page - 1) * pageSize, page * pageSize), totalCount: rows.length }}
        pagination={{
          page,
          rowsPerPage: pageSize,
          handlePagination: setPage,
          handleRowsPerPage: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
      />
      <Typography variant="caption">{t('workflowPerformance.observationHint')}</Typography>
      {report.excludedCaseCount > 0 && (
        <Typography variant="caption">
          {t('workflowPerformance.excluded', { count: report.excludedCaseCount })}
        </Typography>
      )}
    </Stack>
  );
}

export function WorkflowPerformanceScreen({ agencyId, workflowId, settingsOnly = false }: {
  agencyId: number; workflowId: string; settingsOnly?: boolean;
}) {
  const id = String(agencyId);
  const { myProfile } = useAuth();
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => ({
    from: localDateTime(Date.now() - 86400000),
    to: localDateTime(Date.now()),
  }));
  const [range, setRange] = useState<ReturnType<typeof reportRange>>(null);
  const [invalid, setInvalid] = useState(false);
  const allowed = Boolean(myProfile?.roles?.split(',').includes(UserRoles.ADMINS));
  const configuration = usePerformanceConfiguration(myProfile?.id, Number(id), workflowId, allowed);
  const canReport = !settingsOnly && allowed && !configuration.isError && Boolean(configuration.data?.agencyEnabled);
  const query = usePerformanceReport(myProfile?.id, Number(id), workflowId, range, canReport);
  useEffect(() => {
    if (!canReport) setRange(null);
  }, [canReport]);
  const load = () => {
    const next = reportRange(draft.from, draft.to);
    setInvalid(!next);
    if (!next) return;
    if (range?.from === next.from && range?.to === next.to) void query.refetch();
    else setRange(next);
  };
  if (!allowed) return <Alert severity="info">{t('workflowPerformance.disabled')}</Alert>;
  return (
    <Box
      sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 3, bgcolor: 'background.paper', '& input': { fontSize: 16 } }}
    >
      <Stack spacing={3}>
        <Typography variant="h4">{t(settingsOnly ? 'workflowPerformance.configurationTitle' : 'workflowPerformance.title')}</Typography>
        {!settingsOnly && <Typography>{t('workflowPerformance.intro')}</Typography>}
        <Link to={settingsOnly
          ? `${routes.dashboard.performance}?agencyId=${agencyId}&workflowId=${encodeURIComponent(workflowId)}`
          : `${routes.dashboard.agencyView.root.replace(':id', id)}/workflows/${encodeURIComponent(workflowId)}/performance`}>
          {t(settingsOnly ? 'workflowPerformance.openReport' : 'workflowPerformance.configure')}
        </Link>
        {configuration.isLoading && <Typography>{t('workflowPerformance.configLoading')}</Typography>}
        {configuration.isError && (
          <Stack spacing={1}>
            <Alert severity="error">{t(performanceError(configuration.error))}</Alert>
            <Button onClick={() => void configuration.refetch()} disabled={configuration.isFetching}>
              {t('workflowPerformance.refresh')}
            </Button>
          </Stack>
        )}
        {!configuration.isError && configuration.data && (
          <>
            {settingsOnly && <CaptureControls
              configuration={configuration.data}
              refreshing={configuration.isFetching}
              onRefresh={() => configuration.refetch()}
            />}
            {settingsOnly && <Box sx={{border: 1, borderColor: 'divider', borderRadius: 2, p: {xs: 2, md: 3}, maxWidth: 960}}>
              <PerformanceSettingsForm report={configuration.data} />
            </Box>}
          </>
        )}

        {!settingsOnly && configuration.data && !configuration.data.agencyEnabled && !configuration.isError && (
          <Alert severity="info">{t('workflowPerformance.enableAgencyHint')}</Alert>
        )}
        {!settingsOnly && <>
          <Typography variant="body2">
            {t('workflowPerformance.timezone', { zone: Intl.DateTimeFormat().resolvedOptions().timeZone })}
          </Typography>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            {(['from', 'to'] as const).map((field) => (
              <TextField
                key={field}
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                label={t(`workflowPerformance.${field}`)}
                value={draft[field]}
                onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
              />
            ))}
            <Button variant="contained" onClick={load} disabled={!canReport || configuration.isFetching || query.isFetching}>
              {t('workflowPerformance.load')}
            </Button>
          </Stack>
        </>}
        {invalid && <Alert severity="error">{t('workflowPerformance.invalidRange')}</Alert>}
        {query.isFetching && <CircularProgress aria-label={t('workflowPerformance.loading')} />}
        {query.isError && <Alert severity="error">{t(performanceError(query.error))}</Alert>}
        {canReport && !query.isError && !query.isFetching && query.data && (
          <SampleReport key={`${query.data.workflowId}:${query.data.generatedAt}`} report={query.data} />
        )}
      </Stack>
    </Box>
  );
}

export function WorkflowPerformanceView() {
  const { id, workflowId } = useParams();
  const { myProfile } = useAuth();
  return <WorkflowPerformanceScreen key={`${myProfile?.id}:${id}:${workflowId}`} agencyId={Number(id)} workflowId={workflowId ?? ''} settingsOnly />;
}
